import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Download, Trash2, Loader2, ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestMyData, requestMyDeletion } from "@/lib/api";
import { toast } from "sonner";

const DataRequestPage = () => {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState("");
  const [result, setResult] = useState(null);

  const run = async (kind) => {
    if (!reference.trim() || !email.trim()) {
      toast.error("Please enter your reference and email.");
      return;
    }
    if (kind === "delete" &&
        !window.confirm("Permanently delete your intake data? This cannot be undone.")) {
      return;
    }
    setLoading(kind);
    setResult(null);
    try {
      const data = kind === "data"
        ? await requestMyData(reference.trim(), email.trim())
        : await requestMyDeletion(reference.trim(), email.trim());
      setResult({ kind, data });
      if (kind === "delete") {
        toast.success(data.status === "deleted" ? "Your data was deleted." : "Request received.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "We couldn't process that request.");
    } finally {
      setLoading("");
    }
  };

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-6 py-16" data-testid="data-request-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient text-white">
            <Shield className="h-5 w-5" />
          </span>
          <h1 className="font-serif text-3xl text-white">Your data & privacy</h1>
        </div>
        <p className="text-slate-400 leading-relaxed">
          Under the EU GDPR and Angola's Lei n.º 22/11, you can access or delete the personal
          data from an application you submitted to us. Enter the reference we emailed you and the
          email you used, then choose an action. We keep intake submissions for up to 24 months
          unless your case becomes active.
        </p>

        <div className="mt-8 rounded-2xl border border-white/10 bg-card p-6 grid gap-4">
          <div>
            <Label className="mb-1.5 block text-slate-300">Reference</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. VWE-CA-260929-BD38"
              className="bg-slate-950 border-slate-700"
              data-testid="data-request-reference"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-slate-300">Email used on the application</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-slate-950 border-slate-700"
              data-testid="data-request-email"
            />
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            <Button
              onClick={() => run("data")}
              disabled={!!loading}
              className="bg-venka-green-light text-slate-950 hover:bg-venka-green"
              data-testid="data-request-get"
            >
              {loading === "data" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
              Get my data
            </Button>
            <Button
              onClick={() => run("delete")}
              disabled={!!loading}
              variant="outline"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="data-request-delete"
            >
              {loading === "delete" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete my data
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-card p-6" data-testid="data-request-result">
            {result.kind === "delete" ? (
              <p className="text-slate-200">{result.data.message}</p>
            ) : (
              <>
                <p className="text-sm text-slate-400 mb-3">
                  Reference <b className="text-slate-100">{result.data.reference}</b> · status{" "}
                  <b className="text-slate-100">{result.data.status}</b> · submitted{" "}
                  {result.data.submitted_at ? new Date(result.data.submitted_at).toLocaleDateString() : "—"}
                </p>
                <pre className="whitespace-pre-wrap break-words rounded-lg bg-slate-950 p-4 text-xs text-slate-300 max-h-[400px] overflow-auto">
                  {JSON.stringify(
                    {
                      personal_data: result.data.personal_data,
                      sensitive_data: result.data.sensitive_data,
                      family: result.data.family,
                      documents: result.data.documents,
                    },
                    null,
                    2
                  )}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default DataRequestPage;
